import { useState } from 'react';
import Sidebar from './Sidebar';
import MentalityOverlay from '../../modules/mentorship/MentalityOverlay';
import type { TabType } from '../../core/navigation/useNavigationState';
import { type BackendUserWithPlan, resolveRole } from '../../userService';
import './Layout.css';

export type LayoutChildProps = {
    showMenu: boolean;
    onMenuClick: () => void;
};

type LayoutProps = {
    children: (props: LayoutChildProps) => React.ReactNode;
    activeTab: TabType;
    onTabChange: (tab: TabType, subTab?: string) => void;
    educationSubTab?: string | undefined;
    networkingSubTab?: string | undefined;
    userData: BackendUserWithPlan | null;
    onChatSelect?: ((projectId: string, chatId: string) => void) | undefined;
    onNewDirectChat?: (() => void) | undefined;
    selectedChatId?: string | undefined;
    selectedProjectId?: string | undefined;
};

function Layout({
    children,
    activeTab,
    onTabChange,
    educationSubTab,
    networkingSubTab,
    userData,
    onChatSelect,
    onNewDirectChat,
    selectedChatId,
    selectedProjectId,
}: LayoutProps) {
    const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
        try { return localStorage.getItem('ui_sidebarCollapsed') === 'true'; } catch { return false; }
    });
    const toggleSidebar = (collapsed: boolean) => {
        setSidebarCollapsed(collapsed);
        try { localStorage.setItem('ui_sidebarCollapsed', String(collapsed)); } catch { /* quota */ }
    };

    return (
        <div className="layout">
            {/* Skip-link for keyboard users — first Tab target on every
             *  page so they can bypass the sidebar nav and jump straight
             *  to the current tab's content. WCAG 2.1 AA (2.4.1). */}
            <a href="#main-content" className="skip-link">
                Skip to main content
            </a>

            {/* iOS 26 Liquid Glass: real elements for Safari toolbar tinting */}
            <div className="safari-tint-top" aria-hidden="true" />
            <div className="safari-tint-bottom" aria-hidden="true" />

            {/* Mobile overlay — closes sidebar when tapping outside */}
            {!sidebarCollapsed && (
                <div
                    className="sidebar-overlay"
                    onClick={() => toggleSidebar(true)}
                />
            )}
            <Sidebar
                activeTab={activeTab}
                onTabChange={onTabChange}
                isOpen={true}
                collapsed={sidebarCollapsed}
                onToggleCollapse={() => toggleSidebar(!sidebarCollapsed)}
                educationSubTab={educationSubTab}
                networkingSubTab={networkingSubTab}
                userData={userData}
                onChatSelect={onChatSelect}
                onNewDirectChat={onNewDirectChat}
                selectedChatId={selectedChatId}
                selectedProjectId={selectedProjectId}
            />
            <main id="main-content" className={`layout-main ${sidebarCollapsed ? 'sidebar-collapsed' : ''}`}>
                {/* NetworkIndicator + NotificationsBell are now rendered
                 *  inside every SurfaceHeader's actions slot (SSOT). No
                 *  floating global chrome in the Layout. */}
                {children({
                    showMenu: true,
                    onMenuClick: () => toggleSidebar(!sidebarCollapsed),
                })}
            </main>
            {/* M3 / M4 — Mentality overlay available on every page for
             * users with any mentorship role. Hidden for users without
             * one (no curated prompts → nothing to surface). */}
            {resolveRole(userData) && (
                <MentalityOverlay viewerRole={resolveRole(userData)!} />
            )}
        </div>
    );
}

export default Layout;
